import cx from 'classnames';
import { format } from 'date-fns';

import { toShortWeekDateFormat } from '@shared/utils/dateFormatters';
import typography from '~styles/typography.scss';
import type { ServiceOccupant } from '../apiHelpers';
import { isReservation } from '../apiHelpers';
import { useServiceSearchParams } from '../hooks/useServiceSearchParams';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import { ActiveReservationCard } from './ActiveReservationCard';
import styles from './OccupantsForDate.scss';
import { SeatedWalkInCard } from './SeatedWalkInCard';
import { useMultiSelectOccupants } from '../hooks/useMultiSelectOccupants';
import { OccupantSelectionCheckbox } from '../OccupantSelectionCheckbox';
import { DATE_PARAM, DATE_PARAM_FORMAT } from '../../../utils/constants';
import { Spinner } from '@components/Spinner';

interface OccupantsForDateProps {
  occupantsForDate: ServiceOccupant[];
  isLoadingOccupants: boolean;
}

export const OccupantsForDate = ({
  occupantsForDate,
  isLoadingOccupants,
}: OccupantsForDateProps) => {
  const { openOccupantDetailsDrawer, searchParams } = useServiceSearchParams();
  const selectedDate =
    searchParams.get(DATE_PARAM) || format(new Date(), DATE_PARAM_FORMAT);

  const {
    occupants,
    selectedOccupant,
    setSelectedFloorPlanId,
    floorPlansBySelectedDate,
  } = useReservationServiceContext();

  const {
    isMultiSelectingOccupants,
    updateOccupantMultiSelection,
    isOccupantMultiSelected,
  } = useMultiSelectOccupants();

  if (isLoadingOccupants) {
    return <Spinner />;
  }

  if (!occupants.length) {
    return (
      <div className={styles.noContent}>
        There are no upcoming reservations to display
      </div>
    );
  }

  return (
    <>
      {occupantsForDate.map((occupant) => {
        const isSelected = Boolean(selectedOccupant?.id === occupant.id);

        const ariaLabel = isReservation(occupant)
          ? `reservation for ${occupant.guest.firstName} ${occupant.guest.lastName}`
          : `walk-in card for ${occupant.firstName} ${occupant.lastName}`;

        const floorPlanId = isReservation(occupant)
          ? occupant.listingFloorPlanId
          : occupant.seatedFloorPlanId;
        const floorPlanName =
          floorPlansBySelectedDate.find((fp) => fp.id === floorPlanId)?.name ||
          '';

        const showDate = occupant.date !== selectedDate;
        const shortWeekDate = showDate
          ? toShortWeekDateFormat(new Date(`${occupant.date} ${occupant.time}`))
          : null;

        return (
          <li
            key={occupant.id}
            className={cx(styles.occupantCard, {
              [styles.multiSelection]: isMultiSelectingOccupants,
            })}
            aria-label={ariaLabel}
          >
            {isMultiSelectingOccupants && (
              <OccupantSelectionCheckbox
                id={occupant.id}
                checked={isOccupantMultiSelected(occupant)}
                onChange={() => updateOccupantMultiSelection(occupant)}
              />
            )}

            <div className={styles.occupantCardContent}>
              {showDate && (
                <span className={cx(typography.t3, styles.shortWeekDate)}>
                  {shortWeekDate}
                </span>
              )}

              {isReservation(occupant) ? (
                <ActiveReservationCard
                  isSelected={isSelected}
                  onClick={() => {
                    openOccupantDetailsDrawer(occupant);
                    if (occupant.listingFloorPlanId) {
                      setSelectedFloorPlanId(occupant.listingFloorPlanId);
                    }
                  }}
                  reservation={occupant}
                  floorPlanName={floorPlanName}
                />
              ) : (
                <SeatedWalkInCard
                  onClick={() => {
                    openOccupantDetailsDrawer(occupant);

                    if (occupant.seatedFloorPlanId) {
                      setSelectedFloorPlanId(occupant.seatedFloorPlanId);
                    }
                  }}
                  walkIn={occupant}
                  floorPlanName={floorPlanName}
                />
              )}
            </div>
          </li>
        );
      })}
    </>
  );
};
